import React, { useState, useEffect } from "react";
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, Button, TextField, Grid, CircularProgress, Chip, Typography,
    Box, TableSortLabel, TablePagination, Dialog, DialogTitle, DialogContent,
    DialogActions, IconButton
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import FolderIcon from "@mui/icons-material/Folder";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PrintIcon from "@mui/icons-material/Print";
import { Snackbar, Alert, MenuItem, Select } from "@mui/material";
import moment from "moment";
import { honorariosAdminLotesJWT, honorariosAdminLoteDetalleJWT, honorariosAdminCambiarEstadoMasivoJWT } from "../../api/endpoints";

const formatCurrency = (val) => {
    const n = parseFloat(val) || 0;
    return `$ ${n.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatDate = (d) => {
    if (!d) return '-';
    const date = new Date(d);
    if (isNaN(date)) return d;
    return date.toLocaleDateString('es-AR');
};

export default function LotesAdminTable() {
    const [loading, setLoading] = useState(false);
    const [lotes, setLotes] = useState([]);
    const [filters, setFilters] = useState({
        fecha_desde: moment().subtract(1, "year").format("YYYY-MM-DD"),
        fecha_hasta: moment().format("YYYY-MM-DD"),
        busqueda: "",
    });

    // Sort
    const [orderBy, setOrderBy] = useState('Fecha_Inicio');
    const [order, setOrder] = useState('desc');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);

    // Detalle dialog
    const [detalleOpen, setDetalleOpen] = useState(false);
    const [detalleLoading, setDetalleLoading] = useState(false);
    const [detalleData, setDetalleData] = useState(null);
    const [snackMsg, setSnackMsg] = useState({ open: false, text: '', severity: 'success' });
    const [actionLoading, setActionLoading] = useState(false);

    const fetchLotes = async (f = filters) => {
        setLoading(true);
        try {
            const r = await honorariosAdminLotesJWT(f.fecha_desde, f.fecha_hasta, f.busqueda);
            if (r.ok) setLotes(r.lotes || []);
        } catch (e) {
            console.error("Error fetching lotes", e);
        }
        setLoading(false);
    };

    useEffect(() => { fetchLotes(); }, []); // eslint-disable-line

    const handleSort = (col) => {
        setOrder(orderBy === col && order === 'asc' ? 'desc' : 'asc');
        setOrderBy(col);
    };

    const sorted = [...lotes].sort((a, b) => {
        let aV = a[orderBy], bV = b[orderBy];
        if (typeof aV === 'string') { aV = aV?.toLowerCase() || ''; bV = bV?.toLowerCase() || ''; }
        if (aV < bV) return order === 'asc' ? -1 : 1;
        if (aV > bV) return order === 'asc' ? 1 : -1;
        return 0;
    });

    const handleVerLote = async (lote) => {
        setDetalleOpen(true);
        setDetalleLoading(true);
        setDetalleData(null);
        try {
            const r = await honorariosAdminLoteDetalleJWT(lote.Id_Exp_Adm);
            if (r.ok) setDetalleData(r);
        } catch { /* ignore */ }
        setDetalleLoading(false);
    };

    const handleMarcarLotePagado = async () => {
        if (!detalleData?.solicitudes?.length) return;
        const ids = detalleData.solicitudes.filter(s => s.Id_Estado !== 1).map(s => s.Id);
        if (!ids.length) { setSnackMsg({ open: true, text: 'Todas las solicitudes ya están pagadas', severity: 'info' }); return; }
        setActionLoading(true);
        try {
            const r = await honorariosAdminCambiarEstadoMasivoJWT(ids, 1);
            if (r.ok) {
                setSnackMsg({ open: true, text: `${r.afectados} solicitudes marcadas como Pagadas`, severity: 'success' });
                // Recargar detalle y lista
                handleVerLote({ Id_Exp_Adm: detalleData.expediente.Id });
                fetchLotes();
            } else {
                setSnackMsg({ open: true, text: r.mensaje || 'Error', severity: 'error' });
            }
        } catch { setSnackMsg({ open: true, text: 'Error de conexión', severity: 'error' }); }
        setActionLoading(false);
    };

    const handleCambiarEstadoSol = async (solId, nuevoEstado) => {
        setActionLoading(true);
        try {
            const r = await honorariosAdminCambiarEstadoMasivoJWT([solId], nuevoEstado);
            if (r.ok) {
                setSnackMsg({ open: true, text: 'Estado actualizado', severity: 'success' });
                handleVerLote({ Id_Exp_Adm: detalleData.expediente.Id });
                fetchLotes();
            }
        } catch { /* ignore */ }
        setActionLoading(false);
    };

    const handleImprimirLote = () => {
        if (!detalleData) return;
        const rows = (detalleData.solicitudes || []).map(s =>
            `<tr><td>${s.Id}</td><td>${s.Fecha}</td><td>${s.Profesional} (${s.Matricula || ''})</td>
             <td>${s.Certificados}</td><td style="text-align:right">${formatCurrency(s.Honorarios)}</td>
             <td>${s.Estado}</td><td>${s.Fecha_Pago || '-'}</td></tr>`
        ).join('');
        const total = formatCurrency((detalleData.solicitudes || []).reduce((sum, s) => sum + (parseFloat(s.Honorarios) || 0), 0));
        const html = `<!DOCTYPE html><html><head><style>
            body{font-family:Arial,sans-serif;margin:30px}
            h2{color:#2e7d32} table{width:100%;border-collapse:collapse;margin:15px 0}
            th,td{border:1px solid #ddd;padding:5px 7px;font-size:12px}
            th{background:#2e7d32;color:white} .total{text-align:right;font-size:15px;font-weight:bold;margin:15px 0}
        </style></head><body>
            <h2>Lote de Pago: ${detalleData.expediente?.Expediente_Adm || ''}</h2>
            ${detalleData.expediente?.Extracto ? `<p>${detalleData.expediente.Extracto}</p>` : ''}
            <table><thead><tr><th>ID</th><th>Fecha</th><th>Profesional</th><th>Certif.</th><th>Honorarios</th><th>Estado</th><th>Fecha Pago</th></tr></thead>
            <tbody>${rows}</tbody></table>
            <p class="total">Total: ${total}</p>
        </body></html>`;
        const iframeId = "print-lote";
        const old = document.getElementById(iframeId);
        if (old) old.parentNode.removeChild(old);
        const iframe = document.createElement("iframe");
        iframe.id = iframeId;
        iframe.style.cssText = "position:fixed;top:-10000px;left:0;width:800px;height:600px;border:none";
        document.body.appendChild(iframe);
        const doc = iframe.contentDocument || iframe.contentWindow.document;
        doc.open(); doc.write(html); doc.close();
        setTimeout(() => {
            iframe.contentWindow.focus();
            iframe.contentWindow.print();
            setTimeout(() => { if (iframe.parentNode) iframe.parentNode.removeChild(iframe); }, 3000);
        }, 300);
    };

    const getEstadoChip = (lote) => {
        if (lote.Pagadas === lote.Total_Solicitudes) return <Chip label="Pagado" size="small" color="success" variant="outlined" />;
        if (lote.En_Curso > 0) return <Chip label="En Curso" size="small" color="info" variant="outlined" />;
        return <Chip label="Pendiente" size="small" variant="outlined" />;
    };

    const columns = [
        { id: 'Expediente_Adm', label: 'Exp. Administrativo' },
        { id: 'Total_Solicitudes', label: 'Solicitudes' },
        { id: 'Total_Abogados', label: 'Abogados' },
        { id: 'Total_Honorarios', label: 'Total Honorarios', align: 'right' },
        { id: 'Pagadas', label: 'Pagadas' },
        { id: 'En_Curso', label: 'En Curso' },
        { id: 'Fecha_Inicio', label: 'Fecha Inicio' },
    ];

    return (
        <div>
            {/* Filtros */}
            <Paper sx={{ p: 2, mb: 2 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid size={{ xs: 12, sm: 3 }}>
                        <TextField label="Desde" type="date" size="small" fullWidth value={filters.fecha_desde}
                            onChange={e => setFilters(f => ({ ...f, fecha_desde: e.target.value }))}
                            InputLabelProps={{ shrink: true }} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 3 }}>
                        <TextField label="Hasta" type="date" size="small" fullWidth value={filters.fecha_hasta}
                            onChange={e => setFilters(f => ({ ...f, fecha_hasta: e.target.value }))}
                            InputLabelProps={{ shrink: true }} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                        <TextField label="Buscar Exp. Adm." size="small" fullWidth value={filters.busqueda}
                            onChange={e => setFilters(f => ({ ...f, busqueda: e.target.value }))}
                            onKeyDown={e => e.key === 'Enter' && fetchLotes()} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 2 }}>
                        <Button variant="contained" fullWidth startIcon={<SearchIcon />} onClick={() => fetchLotes()}>
                            Buscar
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            {/* Stats */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Paper sx={{ p: 1.5, flex: 1, textAlign: 'center' }}>
                    <Typography variant="h5" fontWeight={700} color="#2e7d32">{lotes.length}</Typography>
                    <Typography variant="caption">Lotes</Typography>
                </Paper>
                <Paper sx={{ p: 1.5, flex: 1, textAlign: 'center' }}>
                    <Typography variant="h5" fontWeight={700} color="primary">{lotes.reduce((s, l) => s + l.Total_Solicitudes, 0)}</Typography>
                    <Typography variant="caption">Solicitudes</Typography>
                </Paper>
                <Paper sx={{ p: 1.5, flex: 1, textAlign: 'center' }}>
                    <Typography variant="h5" fontWeight={700} color="#f57c00">{formatCurrency(lotes.reduce((s, l) => s + (parseFloat(l.Total_Honorarios) || 0), 0))}</Typography>
                    <Typography variant="caption">Total Honorarios</Typography>
                </Paper>
            </Box>

            {/* Tabla */}
            <TableContainer component={Paper}>
                {loading ? (
                    <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>
                ) : (
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                {columns.map(col => (
                                    <TableCell key={col.id} align={col.align} sortDirection={orderBy === col.id ? order : false}>
                                        <TableSortLabel active={orderBy === col.id} direction={orderBy === col.id ? order : 'asc'} onClick={() => handleSort(col.id)}>
                                            {col.label}
                                        </TableSortLabel>
                                    </TableCell>
                                ))}
                                <TableCell>Estado</TableCell>
                                <TableCell align="center">Acciones</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sorted.length === 0 ? (
                                <TableRow><TableCell colSpan={9} align="center">No se encontraron lotes</TableCell></TableRow>
                            ) : (
                                sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((lote, idx) => (
                                    <TableRow key={lote.Id_Exp_Adm} hover onClick={() => handleVerLote(lote)}
                                        sx={{ cursor: 'pointer', backgroundColor: idx % 2 === 0 ? 'rgba(0,0,0,0.02)' : 'transparent' }}>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <FolderIcon fontSize="small" sx={{ color: '#2e7d32' }} />
                                                <strong>{lote.Expediente_Adm}</strong>
                                            </Box>
                                            {lote.Extracto && <Typography variant="caption" color="text.secondary">{lote.Extracto}</Typography>}
                                        </TableCell>
                                        <TableCell>{lote.Total_Solicitudes}</TableCell>
                                        <TableCell>{lote.Total_Abogados}</TableCell>
                                        <TableCell align="right">{formatCurrency(lote.Total_Honorarios)}</TableCell>
                                        <TableCell>{lote.Pagadas}</TableCell>
                                        <TableCell>{lote.En_Curso}</TableCell>
                                        <TableCell>{formatDate(lote.Fecha_Inicio)}</TableCell>
                                        <TableCell>{getEstadoChip(lote)}</TableCell>
                                        <TableCell align="center" onClick={e => e.stopPropagation()}>
                                            <IconButton size="small" onClick={() => handleVerLote(lote)}><VisibilityIcon fontSize="small" /></IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                )}
                {sorted.length > 0 && (
                    <TablePagination component="div" count={sorted.length} page={page}
                        onPageChange={(e, p) => setPage(p)} rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
                        rowsPerPageOptions={[10, 25, 50]} labelRowsPerPage="Por página:"
                        labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`} />
                )}
            </TableContainer>

            {/* Detalle Lote Dialog */}
            <Dialog open={detalleOpen} onClose={() => setDetalleOpen(false)} maxWidth="lg" fullWidth
                PaperProps={{ sx: { borderRadius: '4px' } }}>
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#2e7d32', color: 'white' }}>
                    <span>
                        {detalleData?.expediente ? `Lote: ${detalleData.expediente.Expediente_Adm}` : 'Detalle del Lote'}
                    </span>
                    <IconButton onClick={() => setDetalleOpen(false)} sx={{ color: 'white' }}><CloseIcon /></IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 3, mt: 1 }}>
                    {detalleLoading ? (
                        <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>
                    ) : detalleData ? (
                        <>
                            {detalleData.expediente && (
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="body2"><strong>Expediente:</strong> {detalleData.expediente.Expediente_Adm}</Typography>
                                    {detalleData.expediente.Extracto && <Typography variant="body2"><strong>Extracto:</strong> {detalleData.expediente.Extracto}</Typography>}
                                    {detalleData.expediente.Iniciador && <Typography variant="body2"><strong>Iniciador:</strong> {detalleData.expediente.Iniciador}</Typography>}
                                </Box>
                            )}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography variant="subtitle1" fontWeight={600}>
                                    Solicitudes ({detalleData.solicitudes?.length || 0})
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Button size="small" variant="outlined" startIcon={<PrintIcon />} onClick={handleImprimirLote}>
                                        Imprimir
                                    </Button>
                                    <Button size="small" variant="contained" color="success" startIcon={<CheckCircleIcon />}
                                        disabled={actionLoading || (detalleData.solicitudes || []).every(s => s.Id_Estado === 1)}
                                        onClick={handleMarcarLotePagado}>
                                        {actionLoading ? 'Procesando...' : 'Marcar Todo Pagado'}
                                    </Button>
                                </Box>
                            </Box>
                            <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                    <TableHead sx={{ backgroundColor: '#E8F5E9' }}>
                                        <TableRow>
                                            <TableCell>ID</TableCell>
                                            <TableCell>Fecha</TableCell>
                                            <TableCell>Profesional</TableCell>
                                            <TableCell>Certificados</TableCell>
                                            <TableCell align="right">Honorarios</TableCell>
                                            <TableCell>Estado</TableCell>
                                            <TableCell>Fecha En Curso</TableCell>
                                            <TableCell>Fecha Pago</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {(detalleData.solicitudes || []).map(sol => (
                                            <TableRow key={sol.Id} hover>
                                                <TableCell>{sol.Id}</TableCell>
                                                <TableCell>{sol.Fecha}</TableCell>
                                                <TableCell>
                                                    {sol.Profesional}
                                                    {sol.Matricula && <Typography variant="caption" display="block" color="text.secondary">Mat: {sol.Matricula}</Typography>}
                                                </TableCell>
                                                <TableCell>{sol.Certificados}</TableCell>
                                                <TableCell align="right">{formatCurrency(sol.Honorarios)}</TableCell>
                                                <TableCell>
                                                    <Select size="small" value={sol.Id_Estado}
                                                        onChange={(e) => handleCambiarEstadoSol(sol.Id, e.target.value)}
                                                        sx={{ minWidth: 100, fontSize: '0.8rem' }}
                                                        disabled={actionLoading}>
                                                        <MenuItem value={4}>Enviada</MenuItem>
                                                        <MenuItem value={2}>En Curso</MenuItem>
                                                        <MenuItem value={1}>Pagada</MenuItem>
                                                    </Select>
                                                </TableCell>
                                                <TableCell>{sol.Fecha_En_Curso || '-'}</TableCell>
                                                <TableCell>{sol.Fecha_Pago || '-'}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <Box sx={{ mt: 2, textAlign: 'right' }}>
                                <Typography variant="h6" color="#2e7d32">
                                    Total: {formatCurrency((detalleData.solicitudes || []).reduce((s, sol) => s + (parseFloat(sol.Honorarios) || 0), 0))}
                                </Typography>
                            </Box>
                        </>
                    ) : (
                        <Typography>No se pudo cargar el detalle</Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDetalleOpen(false)}>Cerrar</Button>
                </DialogActions>
            </Dialog>
            <Snackbar open={snackMsg.open} autoHideDuration={4000} onClose={() => setSnackMsg(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert severity={snackMsg.severity} onClose={() => setSnackMsg(s => ({ ...s, open: false }))} variant="filled">
                    {snackMsg.text}
                </Alert>
            </Snackbar>
        </div>
    );
}
